'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { toast } from 'react-toastify'
import { getCurrentCartService } from '@/services/domain/cart/CartService'
import { getCartNotFound } from '@/services/domain/cart/cartErrors'
import type { ICart } from '~/schemas/cart/cart'
import { isContractError } from '~/schemas/errorSchemas'

export const CART_NOT_FOUND_MESSAGE = 'Seu carrinho não está mais aberto: ele pode ter virado pedido ou expirado.'

/**
 * Cache do carrinho no React Query (`['cart', 'current']`), compartilhado pelos hooks do domínio. A `useCartStore` é
 * gravada pelos services (inclusive no 409 `cartNotFound`); aqui fica só o cache e o aviso ao cliente.
 * `handleCartError` devolve `false` para os erros que não são `cartNotFound`. Resposta 2xx fora do contrato (a ação pode
 * ter acontecido no servidor) revalida o carrinho pelo `cart/current`; o aviso genérico já saiu no service.
 */
export function useCartCache() {
    const queryClient = useQueryClient()

    const setCartCache = useCallback(
        (cart: ICart | null) => queryClient.setQueryData(['cart', 'current'], cart),
        [queryClient],
    )

    const handleCartError = useCallback(
        (error: unknown): boolean => {
            if (isContractError(error)) {
                void queryClient
                    .fetchQuery({ queryKey: ['cart', 'current'], queryFn: getCurrentCartService, staleTime: 0 })
                    .catch(() => null)
                return false
            }
            const notFound = getCartNotFound(error)
            if (!notFound) return false
            setCartCache(notFound.cart)
            toast.info(CART_NOT_FOUND_MESSAGE, { autoClose: 4000 })
            return true
        },
        [setCartCache, queryClient],
    )

    return { setCartCache, handleCartError }
}
